import { Link } from "wouter";
import { useI18n } from "../lib/i18n";
import {
  ShieldCheck, BadgeCheck, Wallet, FileSignature, Star, Gavel,
  AlertTriangle, CheckCircle2, XCircle, LifeBuoy, Flag, Lock, MessageSquare,
} from "lucide-react";

/**
 * v3.9.53 (Blueprint Stage 2 / C3) — Trust & Safety center. Explains how the
 * platform protects members (verification, escrow, contracts, disputes), how to
 * transact safely, the red flags to avoid, what the verified badges mean, and
 * where to go when something goes wrong. Built in the existing KontrataPH theme.
 */
const protections = [
  { Icon: BadgeCheck, t: "Identity & business verification", d: "Members can verify their ID, worker skills (KYC), and company registration to earn trust badges shown across the marketplace." },
  { Icon: Wallet, t: "Escrow-protected payments", d: "Funds for a deal are held safely in escrow and only released to the worker once you approve the completed work." },
  { Icon: FileSignature, t: "Clear contracts & milestones", d: "Agree on scope, milestones, and terms in writing before work starts — everything is tracked in one place." },
  { Icon: Star, t: "Ratings & reviews", d: "Two-way reviews after every completed deal help you judge who you're dealing with before you commit." },
  { Icon: Gavel, t: "Dispute resolution", d: "If a deal goes wrong, either side can open a dispute and our team reviews the evidence and helps resolve it." },
  { Icon: ShieldCheck, t: "Moderation & reporting", d: "Listings and profiles are moderated, and you can flag anything suspicious for review at any time." },
];

const dos = [
  "Keep conversations and payments on KontrataPH — that's what escrow and dispute protection cover.",
  "Look for verified badges, ratings, and reviews before you transact.",
  "Agree on scope, price, and milestones in writing before work begins.",
  "Use escrow for payments and only release funds once the work is approved.",
  "Report anything that feels off — a listing, a message, or a profile.",
];
const donts = [
  "Never pay outside the platform or send money by direct bank transfer, e-wallet, or cash on request.",
  "Be wary of deals that seem too good to be true or that pressure you to act fast.",
  "Don't pay upfront \"processing\", \"reservation\", or \"placement\" fees to another member.",
  "Never share your password, OTP, or one-time codes — staff will never ask for them.",
  "Don't move a deal to an outside chat app to \"save on fees\" — you lose all protection.",
];

const badges = [
  { Icon: BadgeCheck, t: "Verified Identity", d: "The member confirmed their government-issued identity." },
  { Icon: ShieldCheck, t: "Verified Company", d: "The organization's registration and details were checked." },
  { Icon: Star, t: "Verified Professional", d: "A specific profession has approved, current supporting evidence. Each profession is reviewed separately." },
];

export default function Safety() {
  const { t } = useI18n();
  return (
    <div className="container-x py-10">
      {/* HERO */}
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="w-14 h-14 rounded-2xl bg-navy text-white grid place-items-center mx-auto mb-3"><ShieldCheck className="w-7 h-7" /></div>
        <h1 className="text-3xl font-extrabold text-navy">{t("safety.title")}</h1>
        <p className="text-mut mt-2">KontrataPH is built to help Filipinos hire and work with confidence. Here's how we protect you — and how to keep yourself safe on every deal.</p>
      </div>

      {/* HOW WE PROTECT YOU */}
      <h2 className="text-xl font-extrabold text-navy mb-4">How KontrataPH protects you</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-10">
        {protections.map((p) => (
          <div key={p.t} className="card p-5">
            <div className="w-11 h-11 rounded-lg bg-navy-50 text-navy grid place-items-center mb-3"><p.Icon className="w-6 h-6" /></div>
            <h3 className="font-bold text-navy">{p.t}</h3>
            <p className="text-sm text-mut mt-1">{p.d}</p>
          </div>
        ))}
      </div>

      {/* SAFE TRANSACTING — do / don't */}
      <div className="grid gap-4 lg:grid-cols-2 mb-10">
        <div className="card p-6">
          <h3 className="font-extrabold text-navy text-lg mb-4 flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-brand-green" />Transact safely</h3>
          <ul className="space-y-3">
            {dos.map((d) => (
              <li key={d} className="flex gap-2.5 text-sm text-navy/90"><CheckCircle2 className="w-4 h-4 text-brand-green shrink-0 mt-0.5" /><span>{d}</span></li>
            ))}
          </ul>
        </div>
        <div className="card p-6">
          <h3 className="font-extrabold text-navy text-lg mb-4 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-brand-red" />Watch for red flags</h3>
          <ul className="space-y-3">
            {donts.map((d) => (
              <li key={d} className="flex gap-2.5 text-sm text-navy/90"><XCircle className="w-4 h-4 text-brand-red shrink-0 mt-0.5" /><span>{d}</span></li>
            ))}
          </ul>
        </div>
      </div>

      {/* BADGES EXPLAINED */}
      <h2 className="text-xl font-extrabold text-navy mb-4">Understanding verified badges</h2>
      <div className="grid gap-4 sm:grid-cols-3 mb-10">
        {badges.map((b) => (
          <div key={b.t} className="card p-5 flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-gold/15 text-brand-gold grid place-items-center shrink-0"><b.Icon className="w-5 h-5" /></div>
            <div><h3 className="font-bold text-navy">{b.t}</h3><p className="text-sm text-mut mt-0.5">{b.d}</p></div>
          </div>
        ))}
      </div>
      <div className="card p-4 mb-10 flex flex-col sm:flex-row sm:items-center gap-3 bg-navy-50/50">
        <BadgeCheck className="w-6 h-6 text-navy shrink-0" />
        <p className="text-sm text-navy/90 flex-1">Verifying your own account earns these badges and helps others trust you — it's free and takes a few minutes.</p>
        <Link href="/get-verified" className="btn-navy h-9 px-4 text-sm shrink-0">Get verified</Link>
      </div>

      {/* IF SOMETHING GOES WRONG */}
      <h2 className="text-xl font-extrabold text-navy mb-4">If something goes wrong</h2>
      <div className="grid gap-4 sm:grid-cols-3 mb-8">
        <div className="card p-5">
          <div className="w-10 h-10 rounded-lg bg-brand-red/10 text-brand-reddark grid place-items-center mb-3"><Flag className="w-5 h-5" /></div>
          <h3 className="font-bold text-navy">Report a listing or user</h3>
          <p className="text-sm text-mut mt-1">Use the flag option on any listing or profile to send it to our moderation team for review.</p>
        </div>
        <div className="card p-5">
          <div className="w-10 h-10 rounded-lg bg-brand-gold/15 text-brand-gold grid place-items-center mb-3"><Gavel className="w-5 h-5" /></div>
          <h3 className="font-bold text-navy">Open a dispute</h3>
          <p className="text-sm text-mut mt-1">On an active deal, open a dispute from your dashboard. Funds stay in escrow while it's reviewed.</p>
        </div>
        <div className="card p-5">
          <div className="w-10 h-10 rounded-lg bg-navy-50 text-navy grid place-items-center mb-3"><LifeBuoy className="w-5 h-5" /></div>
          <h3 className="font-bold text-navy">Contact support</h3>
          <p className="text-sm text-mut mt-1">Our team can help with account, payment, and safety concerns you can't resolve directly.</p>
          <Link href="/support" className="text-sm text-brand-red font-semibold inline-flex items-center gap-1 mt-2">Go to Help Center <MessageSquare className="w-3.5 h-3.5" /></Link>
        </div>
      </div>

      <div className="card p-5 flex items-start gap-3 border-navy-500">
        <Lock className="w-5 h-5 text-navy shrink-0 mt-0.5" />
        <p className="text-sm text-navy/90">Your privacy matters. Review how we handle your data in our <Link href="/legal/privacy" className="text-brand-red font-semibold">Privacy Policy</Link> and the rules of the platform in our <Link href="/legal/terms" className="text-brand-red font-semibold">Terms of Service</Link>.</p>
      </div>
    </div>
  );
}
